import { useEffect, useState } from "react";

/** Slide-over drawer used for Add/Edit forms */
export function MumDrawer({ open, onClose, title, children, testid = "mum-drawer" }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40" data-testid={testid}>
      <div className="absolute inset-0 bg-[var(--mum-midnight)]/40 backdrop-blur-sm" onClick={onClose} />
      <aside className="absolute right-0 top-0 h-full w-full sm:max-w-lg bg-[var(--mum-paper)] border-l border-[var(--mum-cornflower)] shadow-2xl overflow-y-auto animate-bloom-in">
        <div className="sticky top-0 bg-[var(--mum-paper)] border-b border-[var(--mum-sky)] flex items-center justify-between px-5 py-4">
          <h2 className="font-display text-2xl text-[var(--mum-sapphire)]">{title}</h2>
          <button type="button" onClick={onClose} className="mum-btn-ghost" data-testid={`${testid}-close`}>Close</button>
        </div>
        <div className="px-5 py-5">{children}</div>
      </aside>
    </div>
  );
}

/** Photo picker that converts files to data URLs */
export function PhotoField({ label, value, onChange, multi = false, testid }) {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const list = await Promise.all(files.map(toDataURL));
      if (multi) onChange([...(value || []), ...list]);
      else onChange(list[0]);
    } finally { setBusy(false); }
    e.target.value = "";
  };
  return (
    <div>
      <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">{label}</span>
      <div className="mt-1.5 flex flex-wrap gap-2 items-center">
        {multi ? (
          (value || []).map((u, i) => (
            <PhotoChip key={i} url={u} onRemove={() => onChange((value || []).filter((_, j) => j !== i))} />
          ))
        ) : (
          value && <PhotoChip url={value} onRemove={() => onChange(null)} />
        )}
        <label className="mum-btn-ghost cursor-pointer" data-testid={testid}>
          {busy ? "Reading…" : "Add photo"}
          <input type="file" accept="image/*" multiple={multi} className="hidden" onChange={pick} />
        </label>
      </div>
    </div>
  );
}

function PhotoChip({ url, onRemove }) {
  return (
    <div className="relative w-16 h-16 rounded-lg overflow-hidden border border-[var(--mum-cornflower)]">
      <img src={url} alt="" className="w-full h-full object-cover" />
      <button type="button" onClick={onRemove} className="absolute top-0 right-0 bg-[var(--mum-sapphire)] text-[var(--mum-ivory)] text-xs w-5 h-5 leading-5 text-center rounded-bl-lg">×</button>
    </div>
  );
}

const MAX_SIDE = 1400;
const MAX_BYTES = 1_400_000;

async function toDataURL(file) {
  const raw = await new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
  if (raw.length < MAX_BYTES) return raw;
  // Re-encode via canvas to stay under cap
  return await compress(raw);
}

function compress(dataUrl) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      if (width > MAX_SIDE || height > MAX_SIDE) {
        const r = Math.min(MAX_SIDE / width, MAX_SIDE / height);
        width = Math.round(width * r);
        height = Math.round(height * r);
      }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);
      let q = 0.85;
      let out = canvas.toDataURL("image/jpeg", q);
      while (out.length > MAX_BYTES && q > 0.4) {
        q -= 0.1;
        out = canvas.toDataURL("image/jpeg", q);
      }
      resolve(out);
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

/** Section title for forms */
export function FormSection({ title, children, className = "" }) {
  return (
    <fieldset className={`space-y-3 ${className}`}>
      {title && <legend className="text-xs uppercase tracking-[0.22em] text-[var(--mum-cobalt)] mb-1">{title}</legend>}
      {children}
    </fieldset>
  );
}
